import { useCallback, useEffect, useState } from 'react';
import {
  cacheUser,
  clearStoredAuth,
  consumeOAuthCallback,
  fetchUserInfo,
  getCachedUser,
  getToken,
  logoutRequest,
  setToken,
} from '../lib/auth.js';

// Session state for the landing page.
//
// `loading` distinguishes "no session" from "haven't checked yet" so the nav
// doesn't flash a Sign In button at a user who is already signed in.
export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { ok, data } = await fetchUserInfo(token);
      if (!ok) {
        // The token is no longer good — drop it rather than retrying forever.
        clearStoredAuth();
        setUser(null);
        setLoading(false);
        return;
      }
      const info = data.user || data;
      setUser(info);
      cacheUser(info);
    } catch {
      // Network failure, not an auth failure: keep the session and show the
      // cached identity so a flaky connection doesn't look like a sign-out.
      setUser(getCachedUser());
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    consumeOAuthCallback();
    loadUser();
  }, [loadUser]);

  const signIn = useCallback(
    (token) => {
      setToken(token);
      return loadUser();
    },
    [loadUser]
  );

  const signOut = useCallback(() => {
    logoutRequest(getToken());
    clearStoredAuth();
    setUser(null);
  }, []);

  return { user, loading, signIn, signOut };
}
